import { FC } from "react";
import { AlertTriangle, Check } from "lucide-react";
import { GenericQuestionGroup, PossibleAnswer } from "../profiling-process/types/interfaces";

type BlockStatus = "completed" | "skipped" | "active" | "upcoming" | "refused";

/** Un bloc est facultatif quand toutes ses questions le sont (step « passable »). */
const isOptionalBlock = (group: GenericQuestionGroup): boolean =>
  group.questions.length > 0 && group.questions.every((question) => question.isRequired === false);

const blockStatus = (
  group: GenericQuestionGroup,
  currentIndex: number,
  index: number,
  answers: Record<string, PossibleAnswer[]>,
  refusedQuestionKeys: string[]
): BlockStatus => {
  // Une réponse du bloc déclenche une kill question : le refus prime sur la position.
  if (group.questions.some((question) => refusedQuestionKeys.includes(question.key))) return "refused";
  if (index === currentIndex) return "active";
  if (index > currentIndex) return "upcoming";

  const hasAnswer = group.questions.some((question) => (answers[question.key] ?? []).length > 0);
  return hasAnswer || !isOptionalBlock(group) ? "completed" : "skipped";
};

const OptionalBadge: FC<{ active: boolean }> = ({ active }) => (
  <span
    className={`ml-auto rounded-full border px-2 py-px text-[11px] font-medium ${
      active ? "border-primary/40 text-primary" : "border-gray-200 text-gray-400"
    }`}
  >
    Facultatif
  </span>
);

interface CopropertySurveyBlocksRailProps {
  visibleSteps: GenericQuestionGroup[];
  currentStep: number;
  answers: Record<string, PossibleAnswer[]>;
  /** Questions dont la réponse déclenche une règle de refus (évaluation front, à titre d'alerte). */
  refusedQuestionKeys?: string[];
}

/**
 * Rail des blocs du questionnaire COPRO_MRI : position dans le questionnaire, blocs
 * complétés/passés/en refus, badge « Facultatif » sur les blocs passables. Purement
 * indicatif — la navigation passe par les boutons du bloc (l'ordre des blocs est imposé).
 */
export const CopropertySurveyBlocksRail: FC<CopropertySurveyBlocksRailProps> = ({
  visibleSteps,
  currentStep,
  answers,
  refusedQuestionKeys = [],
}) => {
  const currentIndex = visibleSteps.findIndex((group) => group.step === currentStep);

  return (
    <div className="flex flex-col gap-1 rounded-2xl border border-gray-200 bg-white p-4">
      <p className="mb-2 ml-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Bloc {currentIndex + 1} sur {visibleSteps.length}
      </p>
      {visibleSteps.map((group, index) => {
        const status = blockStatus(group, currentIndex, index, answers, refusedQuestionKeys);
        const isCurrent = index === currentIndex;
        const optional = isOptionalBlock(group);

        return (
          <div
            key={group.step}
            className={`flex items-center gap-2.5 rounded-lg px-3 py-2.5 ${
              status === "active" ? "bg-primary/10" : status === "refused" && isCurrent ? "bg-red-50" : ""
            }`}
          >
            {status === "refused" && <AlertTriangle className="h-4 w-4 flex-none text-red-600" />}
            {status === "completed" && <Check className="h-4 w-4 flex-none text-primary" />}
            {status === "active" && <span className="h-2 w-2 flex-none rounded-full bg-primary" />}
            {(status === "upcoming" || status === "skipped") && (
              <span className="h-2 w-2 flex-none rounded-full bg-gray-300" />
            )}
            <span
              className={`text-sm ${
                status === "refused"
                  ? `text-red-700 ${isCurrent ? "font-bold" : ""}`
                  : status === "active"
                  ? "font-bold text-primary"
                  : status === "completed"
                    ? "text-gray-700"
                    : status === "skipped"
                      ? "text-gray-400 line-through"
                      : "text-gray-500"
              }`}
            >
              {group.title}
            </span>
            {status === "refused" ? (
              <span className="ml-auto rounded-full border border-red-200 bg-white px-2 py-px text-[11px] font-medium text-red-600">
                Refus
              </span>
            ) : status === "skipped" ? (
              <span className="ml-auto rounded-full border border-gray-200 px-2 py-px text-[11px] font-medium text-gray-400">
                Passé
              </span>
            ) : (
              optional && status !== "completed" && <OptionalBadge active={status === "active"} />
            )}
          </div>
        );
      })}
    </div>
  );
};
